import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const getStorageKey = (orgId) => orgId ? `whatsNewLastRead_${orgId}` : 'whatsNewLastRead';

// Category badge colors for What's New
const CATEGORY_STYLES = {
  'New Feature':  { bg: '#dcfce7', color: '#166534', label: '✨ New Feature' },
  'Improvement':  { bg: '#dbeafe', color: '#1e40af', label: '⬆️ Improvement' },
  'Bug Fix':      { bg: '#fef3c7', color: '#92400e', label: '🔧 Bug Fix' },
};

// Category badge colors for Tips
const TIP_CATEGORY_STYLES = {
  'Pairings':     { bg: '#ede9fe', color: '#5b21b6' },
  'Players':      { bg: '#dbeafe', color: '#1e40af' },
  'Scheduling':   { bg: '#dcfce7', color: '#166534' },
  'Scoring':      { bg: '#fef3c7', color: '#92400e' },
  'General':      { bg: '#f3f4f6', color: '#374151' },
  'Settings':     { bg: '#fce7f3', color: '#9d174d' },
  'Games':        { bg: '#ffedd5', color: '#9a3412' },
};

function formatDate(dateStr) {
  // Parse parts directly to avoid UTC timezone shift
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-').map(Number);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function WhatsNewModal({ isOpen, onClose, onMarkRead, orgId }) {
  const [activeTab, setActiveTab] = useState('whats-new');
  const [whatsNew, setWhatsNew] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedTipCategory, setExpandedTipCategory] = useState(null);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [wnRes, tipsRes] = await Promise.all([
        fetch(`${API_URL}/content/whats-new`),
        fetch(`${API_URL}/content/tips`)
      ]);
      if (wnRes.ok) setWhatsNew(await wnRes.json());
      if (tipsRes.ok) setTips(await tipsRes.json());
    } catch (err) {
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchContent();
      // Mark as read — store current timestamp per org
      const now = new Date().toISOString();
      localStorage.setItem(getStorageKey(orgId), now);
      if (onMarkRead) onMarkRead();
    }
  }, [isOpen, fetchContent, onMarkRead, orgId]);

  if (!isOpen) return null;

  // Group tips by category
  const tipsByCategory = tips.reduce((acc, tip) => {
    const cat = tip.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tip);
    return acc;
  }, {});

  const catStyle = (cat) => TIP_CATEGORY_STYLES[cat] || TIP_CATEGORY_STYLES['General'];

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', justifyContent: 'flex-end', alignItems: 'stretch'
      }}
      onClick={onClose}
    >
      {/* Slide-out Panel */}
      <div
        style={{
          width: '100%', maxWidth: 520, background: 'white',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          animation: 'slideInRight 0.2s ease-out'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 0',
          borderBottom: '1px solid #e5e7eb',
          background: 'white',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                🔔 Updates & Tips
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                New features and helpful tips for your golf group
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: '#f3f4f6', border: 'none', borderRadius: 8,
                width: 32, height: 32, cursor: 'pointer', fontSize: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#6b7280', flexShrink: 0
              }}
            >×</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { id: 'whats-new', label: "What's New" },
              { id: 'tips', label: 'Tips & Tricks' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px', background: 'none', border: 'none',
                  borderBottom: activeTab === tab.id ? '3px solid #15803d' : '3px solid transparent',
                  color: activeTab === tab.id ? '#15803d' : '#6b7280',
                  fontWeight: activeTab === tab.id ? 700 : 500,
                  cursor: 'pointer', fontSize: 14, transition: 'all 0.15s'
                }}
              >{tab.label}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
              Loading...
            </div>
          )}
          {error && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, fontSize: 14 }}>
              {error}
            </div>
          )}

          {/* WHAT'S NEW TAB */}
          {!loading && activeTab === 'whats-new' && (
            <div>
              {whatsNew.length === 0 && !error && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 14 }}>
                  No updates yet — check back soon!
                </div>
              )}
              {whatsNew.map((item, i) => {
                const catStyle = CATEGORY_STYLES[item.category] || CATEGORY_STYLES['New Feature'];
                return (
                  <div
                    key={item.id}
                    style={{
                      marginBottom: i < whatsNew.length - 1 ? 20 : 0,
                      paddingBottom: i < whatsNew.length - 1 ? 20 : 0,
                      borderBottom: i < whatsNew.length - 1 ? '1px solid #f3f4f6' : 'none'
                    }}
                  >
                    {/* Date + badges row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                        {formatDate(item.date || item.published_at)}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                        background: catStyle.bg, color: catStyle.color
                      }}>
                        {catStyle.label}
                      </span>
                      {item.version_tag && (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                          background: '#f3f4f6', color: '#6b7280'
                        }}>
                          {item.version_tag}
                        </span>
                      )}
                    </div>
                    {/* Title */}
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                      {item.title}
                    </div>
                    {/* Body */}
                    <div style={{
                      fontSize: 14, color: '#4b5563', lineHeight: 1.65,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {item.body}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TIPS & TRICKS TAB */}
          {!loading && activeTab === 'tips' && (
            <div>
              {tips.length === 0 && !error && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 14 }}>
                  Tips coming soon!
                </div>
              )}
              {Object.entries(tipsByCategory).map(([category, categoryTips]) => {
                const cs = catStyle(category);
                const isExpanded = expandedTipCategory === category || expandedTipCategory === null;
                return (
                  <div key={category} style={{ marginBottom: 16 }}>
                    {/* Category header */}
                    <button
                      onClick={() => setExpandedTipCategory(
                        expandedTipCategory === category ? null : category
                      )}
                      style={{
                        width: '100%', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '10px 14px', borderRadius: 10,
                        background: cs.bg, border: 'none', cursor: 'pointer', marginBottom: 2
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: cs.color }}>
                        {category}
                        <span style={{ fontWeight: 500, marginLeft: 6, opacity: 0.7 }}>
                          ({categoryTips.length})
                        </span>
                      </span>
                      <span style={{ color: cs.color, fontSize: 16 }}>
                        {expandedTipCategory === category ? '▲' : '▼'}
                      </span>
                    </button>

                    {/* Tips in category */}
                    {expandedTipCategory === category && categoryTips.map((tip, i) => (
                      <div
                        key={tip.id}
                        style={{
                          padding: '14px 16px',
                          background: i % 2 === 0 ? '#fafafa' : 'white',
                          border: '1px solid #f3f4f6',
                          borderRadius: 8,
                          marginBottom: 6
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                          💡 {tip.title}
                        </div>
                        <div style={{
                          fontSize: 13, color: '#4b5563', lineHeight: 1.65,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {tip.body}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// Helper: call this on app load to calculate unread count
// Returns number of What's New items newer than last read timestamp
// ============================================================================
export function getUnreadCount(whatsNewItems, orgId) {
  const lastRead = localStorage.getItem(getStorageKey(orgId));
  if (!lastRead) return whatsNewItems.length; // never opened = all unread
  return whatsNewItems.filter(item => {
    const itemDate = new Date(item.date || item.published_at);
    const readDate = new Date(lastRead);
    return itemDate > readDate;
  }).length;
}
